import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Clock, Calendar } from "lucide-react";
import { restaurantSettings, isRestaurantOpen } from "../config/restaurant";
import { Context } from "../context";

const Instagram = ({ size = 18, className = "" }) => (
  <svg
    width={size}
    height={size}
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const Facebook = ({ size = 18, className = "" }) => (
  <svg
    width={size}
    height={size}
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

export const Footer: React.FC = () => {
  const { t } = useTranslation("common");
  const isOpen = isRestaurantOpen();
  const { ui } = useContext(Context);

  return (
    <footer className="bg-stone-950 text-stone-300 border-t border-stone-800/80 pt-16 pb-10 mt-20 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* EN: Top Booking Call-to-Action Banner */}
        <div className="bg-stone-900/60 border border-stone-800 rounded-2xl p-8 mb-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl font-serif text-stone-100 mb-1">
              {t("footer.bookingBanner.title")}
            </h3>
            <p className="text-stone-400 text-sm">
              {t("footer.bookingBanner.description")}
            </p>
          </div>
          <button
            onClick={() => ui.setIsBookingOpen(true)}
            className="shrink-0 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 font-medium px-6 py-3 rounded-xl transition-all flex items-center gap-2 text-sm cursor-pointer"
          >
            <Calendar size={16} />
            <span>{t("footer.bookingBanner.button")}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* EN: Column 1: Brand & Concept */}
          <div>
            {/* EN: Logo Link with reduced bottom margin */}
            <Link
              to="/"
              className="inline-flex items-center gap-3 text-2xl font-serif tracking-wide text-stone-900 dark:text-stone-100 group transition-opacity hover:opacity-90 mb-1"
            >
              <img
                src="/logo.svg"
                alt={restaurantSettings.name}
                className="h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105 dark:brightness-110"
              />
            </Link>

            {/* EN: Description Text right below logo */}
            <p className="text-xs sm:text-sm text-stone-600 dark:text-stone-300 leading-relaxed font-light mb-5">
              {t("footer.brandDescription")}
            </p>

            {/* EN: Social Links */}
            <div className="flex items-center gap-2.5">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="p-2.5 bg-stone-200/60 dark:bg-stone-900/80 rounded-xl text-stone-700 dark:text-stone-300 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-stone-300/70 dark:hover:bg-stone-800 border border-stone-300/60 dark:border-stone-800 transition-all duration-200 shadow-sm hover:scale-105"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="p-2.5 bg-stone-200/60 dark:bg-stone-900/80 rounded-xl text-stone-700 dark:text-stone-300 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-stone-300/70 dark:hover:bg-stone-800 border border-stone-300/60 dark:border-stone-800 transition-all duration-200 shadow-sm hover:scale-105"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* EN: Column 2: Navigation Links */}
          <div>
            <h4 className="text-stone-200 font-serif text-base mb-4 border-b border-stone-800 pb-2 inline-block">
              {t("footer.sections.navigation")}
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <Link to="/" className="hover:text-amber-300 transition-colors">
                  {t("footer.links.home")}
                </Link>
              </li>
              <li>
                <a
                  href="/menu"
                  className="hover:text-amber-300 transition-colors"
                >
                  {t("footer.links.menu")}
                </a>
              </li>
              <li>
                <button
                  onClick={() => ui.setIsBookingOpen(true)}
                  className="hover:text-amber-300 transition-colors text-left cursor-pointer"
                >
                  {t("footer.links.bookings")}
                </button>
              </li>
              <li>
                <Link
                  to="/allergens"
                  className="hover:text-amber-300 transition-colors"
                >
                  {t("footer.links.allergens")}
                </Link>
              </li>
            </ul>
          </div>

          {/* EN: Column 3: Opening Hours */}
          <div>
            <h4 className="text-stone-200 font-serif text-base mb-4 border-b border-stone-800 pb-2 inline-block">
              {t("footer.sections.workingHours")}
            </h4>
            <div className="space-y-3 text-xs">
              <div className="flex items-center gap-2">
                <Clock size={15} className="text-amber-400 shrink-0" />
                <span>
                  {restaurantSettings.openingHours.restaurant.display}
                </span>
              </div>

              <div className="pt-2">
                {isOpen ? (
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] bg-emerald-950/60 text-emerald-400 border border-emerald-800/60">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    {t("footer.status.open")}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] bg-stone-900 text-stone-300 border border-stone-700">
                    {t("footer.status.closed")}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* EN: Column 4: Location & Contact */}
          <div>
            <h4 className="text-stone-200 font-serif text-base mb-4 border-b border-stone-800 pb-2 inline-block">
              {t("footer.sections.contact")}
            </h4>
            <ul className="space-y-3 text-xs">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-amber-400 shrink-0" />
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(
                    restaurantSettings.fullAddress
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-amber-300 transition-colors"
                >
                  {restaurantSettings.fullAddress}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-amber-400 shrink-0" />
                <a
                  href={`tel:${restaurantSettings.phoneRaw}`}
                  className="hover:text-amber-300 transition-colors font-mono"
                >
                  {restaurantSettings.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* EN: Bottom Copyright, Legal Links, and Payment methods */}
        <div className="border-t border-stone-800/60 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] text-stone-500">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-stone-300">
              © {new Date().getFullYear()}{" "}
              {t("footer.copyright", { appName: restaurantSettings.name })}
            </p>
            <span className="hidden sm:inline w-1 h-1 bg-stone-700 rounded-full"></span>
            <div className="flex items-center gap-3">
              <Link
                to="/privacy-policy"
                className="text-stone-200 hover:text-amber-400 transition-colors"
              >
                {t("footer.privacyPolicy")}
              </Link>
              <span className="w-1 h-1 bg-stone-700 rounded-full"></span>
              <Link
                to="/terms-of-service"
                className="text-stone-200 hover:text-amber-400 transition-colors"
              >
                {t("footer.termsOfService")}
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span>{t("footer.paymentMethods")}</span>
            <span className="w-1 h-1 bg-stone-700 rounded-full"></span>
            <span>BLIK / Card / Apple Pay</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
